import { z } from "@eisox/zod";

const LAT_MIN = -90;
const LAT_MAX = 90;
const LNG_MIN = -180;
const LNG_MAX = 180;
const ZOOM_MIN = 0;
const ZOOM_MAX = 22;
const COORDINATE_DECIMALS = 5;

export const buildingsMapSearchSchema = z.object({
  lat: z.coerce.number<number>().min(LAT_MIN).max(LAT_MAX).optional().catch(undefined),
  lng: z.coerce.number<number>().min(LNG_MIN).max(LNG_MAX).optional().catch(undefined),
  zoom: z.coerce.number<number>().int().min(ZOOM_MIN).max(ZOOM_MAX).optional().catch(undefined),
});

export type BuildingsMapSearch = z.infer<typeof buildingsMapSearchSchema>;

export type CompleteBuildingsMapView = {
  lat: number;
  lng: number;
  zoom: number;
};

let lastBuildingsMapSearch: CompleteBuildingsMapView | undefined;

export const isCompleteMapView = (search: BuildingsMapSearch): search is CompleteBuildingsMapView =>
  search.lat !== undefined && search.lng !== undefined && search.zoom !== undefined;

const roundCoordinate = (value: number): number => Number.parseFloat(value.toFixed(COORDINATE_DECIMALS));

export const toMapSearch = (view: { lat: number; lng: number; zoom: number }): CompleteBuildingsMapView => ({
  lat: roundCoordinate(view.lat),
  lng: roundCoordinate(view.lng),
  zoom: Math.round(view.zoom),
});

export const isSameMapSearch = (a: BuildingsMapSearch, b: BuildingsMapSearch): boolean => {
  if (!isCompleteMapView(a) || !isCompleteMapView(b)) {
    return false;
  }

  const roundedA = toMapSearch(a);
  const roundedB = toMapSearch(b);

  return roundedA.lat === roundedB.lat && roundedA.lng === roundedB.lng && roundedA.zoom === roundedB.zoom;
};

export const setLastBuildingsMapSearch = (view: CompleteBuildingsMapView): void => {
  lastBuildingsMapSearch = toMapSearch(view);
};

export const getLastBuildingsMapSearch = (): CompleteBuildingsMapView | undefined => lastBuildingsMapSearch;
